import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Maximize, Minimize } from 'lucide-react';
import { useAdminAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

const ExperienceTile = ({ exp, index }) => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const bgUrl = resolveUrl(exp.background_url);

  return (
    <button
      onClick={() => navigate(`/the-show/${exp.id}?from=showroom`)}
      className="group relative w-full aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0077FF]/50 focus:ring-offset-2 focus:ring-offset-[#060a14]"
      style={{
        animationDelay: `${0.15 + index * 0.1}s`,
        animation: 'showroom-tile-in 0.6s ease-out both',
      }}
      data-testid={`showroom-tile-${exp.id}`}
    >
      {bgUrl && (
        <img
          src={bgUrl}
          alt={exp.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out
            ${imgLoaded ? 'opacity-100' : 'opacity-0'}
            group-hover:scale-[1.06] group-focus:scale-[1.06]`}
          onLoad={() => setImgLoaded(true)}
          draggable={false}
        />
      )}
      {!imgLoaded && (
        <div className="absolute inset-0 bg-white/[0.03]" />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent
        group-hover:from-black/70 group-hover:via-black/10 transition-all duration-500" />

      <div className="absolute inset-0 border border-white/[0.06] rounded-2xl
        group-hover:border-[#0077FF]/30 transition-colors duration-400" />

      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight
          group-hover:text-[#4DB8FF] transition-colors duration-300">
          {exp.name}
        </h3>
        {exp.vertical && (
          <p className="text-[11px] text-white/35 mt-1 uppercase tracking-[0.15em] font-medium
            group-hover:text-white/50 transition-colors duration-300">
            {exp.vertical}
          </p>
        )}
      </div>

      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100
        transition-all duration-300 translate-x-1 group-hover:translate-x-0">
        <div className="w-8 h-8 rounded-full bg-[#0077FF]/20 border border-[#0077FF]/30
          flex items-center justify-center backdrop-blur-sm">
          <svg className="w-3.5 h-3.5 text-[#4DB8FF]" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </button>
  );
};

const ShowroomGalleryPage = () => {
  const navigate = useNavigate();
  const auth = useAdminAuth();
  const isAdmin = auth?.authType === 'admin';
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/showroom/gallery`, { credentials: 'include' });
        if (res.status === 401) {
          navigate('/admin/login?redirect=/admin/showroom', { replace: true });
          return;
        }
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        setExperiences(data.experiences || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center" data-testid="showroom-gallery-loading">
        <Loader2 className="w-8 h-8 text-[#0077FF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center px-4" data-testid="showroom-gallery-error">
        <div className="text-center">
          <p className="text-white/50 text-sm mb-4">{error}</p>
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-[#0077FF] text-white text-sm font-medium hover:bg-[#0066DD] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#060a14] overflow-y-auto" data-testid="showroom-gallery">
      <style>{`
        @keyframes showroom-tile-in {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes showroom-heading-in {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 35%, rgba(0,119,255,0.04) 0%, transparent 60%), radial-gradient(ellipse at 70% 65%, rgba(0,119,255,0.03) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.015) 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
      </div>

      <div className="relative z-10 min-h-full flex flex-col">
        <div className="flex items-center justify-between px-6 sm:px-10 pt-4">
          {isAdmin ? (
            <button
              onClick={() => navigate('/admin/experiences')}
              className="flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors text-sm"
              data-testid="showroom-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Admin</span>
            </button>
          ) : <div />}
          <div className="flex items-center gap-3">
            {document.fullscreenEnabled && (
              <button
                onClick={toggleFullscreen}
                className="flex items-center gap-1.5 text-white/30 hover:text-white/60 transition-colors text-sm"
                data-testid="showroom-fullscreen-btn"
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
              </button>
            )}
            <img
              src="/tapelligence_logo_transparent.png"
              alt="Tapelligence"
              className="h-5 opacity-30"
              draggable={false}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-4 sm:py-6">
          <div className="text-center mb-5 sm:mb-7" style={{ animation: 'showroom-heading-in 0.5s ease-out both' }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Tapelligence <span className="text-[#0077FF]">Showroom</span>
            </h1>
            <p className="text-white/25 text-sm sm:text-base mt-3 tracking-wide">
              Explore our interactive experiences
            </p>
          </div>

          <div className="w-full max-w-5xl grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
            {experiences.map((exp, i) => (
              <ExperienceTile key={exp.id} exp={exp} index={i} />
            ))}
          </div>

          {experiences.length === 0 && (
            <p className="text-white/30 text-sm mt-8">No experiences available.</p>
          )}
        </div>

        <div className="px-6 sm:px-10 pb-6 text-center">
          <p className="text-[10px] text-white/15 tracking-[0.2em] uppercase">
            Tapelligence — Interactive Experience Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShowroomGalleryPage;
